import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  TextField,
} from "@mui/material";
import { useContext, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  addCell,
  manuallyCreateFieldState,
  selectAllLensEditorReviewConfigs,
  selectLensEditorForm,
  selectLensEditorFormSections,
  updateCell,
  updateCellAiConfig,
  updateFieldAiConfig,
  updateFormField,
  upsertFieldTagConfig,
} from "redux/slices/lensEditor";
import PSelect from "components/PSelect";
import SettingsIcon from "@mui/icons-material/Settings";
import { P } from "theme/typography";
import { useSnackbar } from "notistack";
import { isValidJSONFormImportObject } from "Backend";
import {
  Cell,
  CellAiConfigView,
  CreateFieldDocument,
  FieldAiConfigView,
  FieldTagConfigView,
  FormField,
} from "generated/graphql";
import FieldPreview from "routes/lens-editor/profile-builder/FieldPreview";
import CellPreview from "routes/lens-editor/profile-builder/CellPreview";
import theme from "theme";
import { runGqlMutation } from "gqlClient";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router";
import { ExtractedGQLError } from "shared/PatternGraphQLError";
import { LensUpdateSnackbarContext } from "contexts/LensUpdateSnackbarContext";

interface FieldImportDialogProps {
  onClose: () => void;
  sourceField?: FormField;
  sourceFormId: number;
  defaultSectionId: number;
}

export const FieldImport = ({ onClose, sourceField, sourceFormId, defaultSectionId }: FieldImportDialogProps) => {
  const [json, setJson] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<number | undefined>(defaultSectionId);
  const sectionOptions = useAppSelector(selectLensEditorFormSections);
  const lensForm = useAppSelector(selectLensEditorForm);
  const allReviewConfigs = useAppSelector(selectAllLensEditorReviewConfigs);
  const [tasksCompleted, setTasksCompleted] = useState<number>(0);
  const [totalTasks, setTotalTasks] = useState<number>(0);
  const [isPending, setIsPending] = useState(false);
  const navigate = useNavigate();
  const { lensId } = useParamNumbers(["lensId"]);
  const { enqueueLensUpdateTimerSnackbar } = useContext(LensUpdateSnackbarContext);

  const [selectedReviewConfigId, setSelectedReviewConfigId] = useState<number>(-1);
  const { enqueueSnackbar } = useSnackbar();

  const jsonField = useRef<{
    field: FormField;
    cellAIConfigs: CellAiConfigView[];
    fieldAIConfig: FieldAiConfigView;
    fieldTagConfig: FieldTagConfigView;
  } | null>(null);
  const dispatch = useAppDispatch();
  const handleClose = () => {
    setJson("");
    setError(null);
    setIsPending(false);
    onClose();
  };

  const attachCellsToNewField = async (fieldId: number, cells: Cell[]) => {
    const cellsByOldCellIds = new Map();
    for (const cell of cells) {
      await dispatch(
        addCell({
          ...cell,
          // System name is always present in the imported cell
          systemName: cell.systemName!,
          fieldId,
          optional: lensForm.live ? true : cell.optional,
          analyticsExclude: cell.analyticsExclude === true,
        })
      )
        .unwrap()
        .then((res) => {
          cellsByOldCellIds.set(cell.id, res.lens.form.createCell.id);
          const newAiConfig = jsonField.current?.cellAIConfigs.find((c) => c.cellId === cell.id);
          return dispatch(
            updateCellAiConfig({
              cellId: res.lens.form.createCell.id,
              additionalPrompt: newAiConfig?.additionalPrompt,
              aiExclude: !!newAiConfig?.aiExclude,
              alternateName: newAiConfig?.alternateName,
              schemaValueName: newAiConfig?.schemaValueName,
              valueLast: newAiConfig?.valueLast ?? false,
              copoutSelectOptions: newAiConfig?.copoutSelectOptions || [],
            })
          );
        })
        .catch((gqlError: ExtractedGQLError | undefined) => {
          enqueueSnackbar(`Unable to add cell - ${gqlError?.message}`, {
            variant: "error",
          });
        });
      setTasksCompleted((prev) => prev + 1);
    }
    return cellsByOldCellIds;
  };

  const handleNewField = async () => {
    if (jsonField.current && jsonField.current.field && sectionOptions[0]) {
      setTotalTasks(jsonField.current.field.cells.length);
      await runGqlMutation(CreateFieldDocument, {
        formId: sourceFormId,
        ...jsonField.current.field,
        reviewConfigId: selectedReviewConfigId === -1 ? null : selectedReviewConfigId,
        sectionId: selectedSectionId === undefined ? sectionOptions[0].id : selectedSectionId,
        analyticsExclude: jsonField.current.field.analyticsExclude === true,
      })
        .then(async (res) => {
          const createdField = res.lens.form.createField;
          if (jsonField.current?.fieldAIConfig) {
            await dispatch(
              updateFieldAiConfig({
                ...jsonField.current?.fieldAIConfig,
                fieldId: createdField.id,
              })
            );
          }
          if (jsonField.current?.fieldTagConfig) {
            await dispatch(
              upsertFieldTagConfig({
                ...jsonField.current?.fieldTagConfig,
                fieldId: createdField.id,
              })
            );
          }
          dispatch(
            manuallyCreateFieldState({
              field: createdField,
            })
          );
          if (jsonField.current) {
            const cellsByOldCellIds = await attachCellsToNewField(createdField.id, jsonField.current?.field.cells);
            await dispatch(
              updateFormField({
                id: createdField.id,
                values: {
                  groupByCellId: cellsByOldCellIds.get(jsonField.current.field.groupByCellId),
                },
                clears: {
                  groupByCellId: jsonField.current.field.groupByCellId === null,
                },
              })
            )
              .unwrap()
              .catch((gqlError: ExtractedGQLError | undefined) => {
                enqueueSnackbar(`Unable to update field after import - ${gqlError?.message}`, {
                  variant: "error",
                });
              });
          }
          navigate(`/lens-editor/${lensId}/profile/sections/${createdField.sectionId}/fields/${createdField.id}`);
          enqueueSnackbar("Created and navigated to field", { variant: "success" });
          lensId && enqueueLensUpdateTimerSnackbar(lensId);
        })
        .catch((gqlError: ExtractedGQLError | undefined) => {
          enqueueSnackbar(`Unable to add field - ${gqlError?.message}`, {
            variant: "error",
          });
        });
      handleClose();
    }
  };

  const handleOverwrite = async () => {
    if (sourceField && jsonField.current) {
      const { exportExclude, groupByCellId, key, name, prompt, promptYesNo, rank, repeating, required } =
        jsonField.current.field;
      setTotalTasks(jsonField.current.field.cells.length + 2);
      await dispatch(
        updateFormField({
          id: sourceField.id,
          values: {
            exportExclude,
            key,
            name,
            prompt,
            promptYesNo,
            rank,
            repeating,
            required,
            reviewConfigId: selectedReviewConfigId === -1 ? null : selectedReviewConfigId,
          },
          clears: {
            key: key === null,
          },
        })
      )
        .unwrap()
        .then(async () => {
          enqueueSnackbar("Field updated", { variant: "success" });
          if (jsonField.current?.fieldAIConfig) {
            await dispatch(
              updateFieldAiConfig({
                ...jsonField.current.fieldAIConfig,
                fieldId: sourceField.id,
              })
            );
          }
          if (jsonField.current?.fieldTagConfig) {
            await dispatch(
              upsertFieldTagConfig({
                ...jsonField.current?.fieldTagConfig,
                fieldId: sourceField.id,
              })
            );
          }
          lensId && enqueueLensUpdateTimerSnackbar(lensId);
        })
        .catch((gqlError: ExtractedGQLError | undefined) => {
          enqueueSnackbar(`Unable to update field - ${gqlError?.message}`, {
            variant: "error",
          });
        });
      setTasksCompleted((prev) => prev + 1);
      const cellsByOldCellIds = new Map();
      for (const importedCell of jsonField.current.field.cells) {
        const matchingCell = sourceField.cells.find((c) => c.systemName === importedCell.systemName);
        if (matchingCell !== undefined) {
          cellsByOldCellIds.set(importedCell.id, matchingCell.id);
          const { id, fieldId, systemName, ...payload } = importedCell;

          await dispatch(
            updateCell({
              id: matchingCell.id,
              values: payload,
              clears: {
                key: importedCell.key === null,
                default: importedCell.default === null,
              },
            })
          )
            .unwrap()
            .then(async () => {
              const newAiConfig = jsonField.current?.cellAIConfigs.find((c) => {
                return c.cellId === importedCell.id;
              });
              await dispatch(
                updateCellAiConfig({
                  cellId: matchingCell.id,
                  additionalPrompt: newAiConfig?.additionalPrompt,
                  aiExclude: !!newAiConfig?.aiExclude,
                  alternateName: newAiConfig?.alternateName,
                  schemaValueName: newAiConfig?.schemaValueName,
                  valueLast: newAiConfig?.valueLast ?? false,
                  copoutSelectOptions: newAiConfig?.copoutSelectOptions || [],
                })
              );
            })
            .catch((gqlError: ExtractedGQLError | undefined) => {
              enqueueSnackbar(`Unable to update cell after import - ${gqlError?.message}`, {
                variant: "error",
              });
            });
        } else {
          await dispatch(
            addCell({
              ...importedCell,
              fieldId: sourceField.id,
              // System name is always present in the imported cell
              systemName: importedCell.systemName!,
              analyticsExclude: importedCell.analyticsExclude === true,
            })
          )
            .unwrap()
            .then((res) => {
              const newAiConfig = jsonField.current?.cellAIConfigs.find((c) => c.cellId === importedCell.id);
              return dispatch(
                updateCellAiConfig({
                  cellId: res.lens.form.createCell.id,
                  additionalPrompt: newAiConfig?.additionalPrompt,
                  aiExclude: !!newAiConfig?.aiExclude,
                  alternateName: newAiConfig?.alternateName,
                  schemaValueName: newAiConfig?.schemaValueName,
                  valueLast: newAiConfig?.valueLast ?? false,
                  copoutSelectOptions: newAiConfig?.copoutSelectOptions ?? [],
                })
              );
            })
            .catch((gqlError: ExtractedGQLError | undefined) => {
              enqueueSnackbar(`Unable to add cell after import - ${gqlError?.message}`, {
                variant: "error",
              });
            });
        }
        setTasksCompleted((prev) => prev + 1);
      }
      await dispatch(
        updateFormField({
          id: sourceField.id,
          values: {
            groupByCellId: cellsByOldCellIds.get(groupByCellId),
          },
          clears: {
            groupByCellId: groupByCellId === null,
          },
        })
      )
        .unwrap()
        .catch((gqlError) => {
          enqueueSnackbar(`Unable to update field after import - ${gqlError?.message}`, {
            variant: "error",
          });
        });
      setTasksCompleted((prev) => prev + 1);
      handleClose();
    }
  };

  const handleImport = () => {
    setIsPending(true);
    if (sourceField && jsonField.current) {
      handleOverwrite();
      return;
    }
    handleNewField();
  };

  return (
    <>
      <DialogTitle>{isPending ? "Importing Field" : "Import Field from JSON"}</DialogTitle>
      <DialogContent>
        {!isPending && (
          <Box>
            {lensForm.live && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Profile is live. Any required cells will be imported as optional.
              </Alert>
            )}
            {jsonField.current ? (
              <>
                <P variant="lgBold">Field Preview</P>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.grey[50],
                    borderRadius: 1,
                    display: "flex",
                    gap: 2,
                    flexDirection: "column",
                    overflowY: "auto",
                    maxHeight: "400px",
                  }}>
                  <FieldPreview field={jsonField.current.field} aiFieldConfig={jsonField.current?.fieldAIConfig} />
                  <Divider />
                  {jsonField.current?.field.cells
                    .slice()
                    .sort((a, b) => a.rank - b.rank)
                    .map((cell) => (
                      <CellPreview
                        cell={cell}
                        key={cell.id}
                        cellAiConfig={jsonField.current?.cellAIConfigs.find((c) => c.cellId === cell.id)}
                      />
                    ))}
                </Box>
              </>
            ) : (
              <TextField
                autoFocus
                error={!!error}
                helperText={error}
                fullWidth
                multiline
                placeholder="Paste JSON here"
                rows={10}
                value={json}
                onChange={(e) => {
                  const value = e.target.value;
                  setJson(value);
                  let parsedValue: any;
                  try {
                    parsedValue = JSON.parse(value);
                  } catch (error) {
                    setSelectedReviewConfigId(-1);
                    setError("Invalid JSON");
                    return;
                  }

                  if (isValidJSONFormImportObject(parsedValue)) {
                    const formField = parsedValue as unknown as {
                      field: FormField;
                      cellAIConfigs: CellAiConfigView[];
                      fieldAIConfig: FieldAiConfigView;
                      fieldTagConfig: FieldTagConfigView;
                    };
                    if (
                      formField.field.reviewConfigId &&
                      allReviewConfigs.find((rc) => rc.id === formField.field.reviewConfigId)
                    ) {
                      setSelectedReviewConfigId(formField.field.reviewConfigId);
                      enqueueSnackbar("Review config found in JSON, selected", { variant: "info" });
                    } else {
                      setSelectedReviewConfigId(-1);
                    }
                    setError(null);
                    jsonField.current = formField;
                  } else {
                    setSelectedReviewConfigId(-1);
                    setError(
                      "JSON failed structure validation. Please make sure the JSON is copied directly from the field."
                    );
                  }
                }}
              />
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1,
                marginTop: 2,
              }}>
              <P variant="lgBold">Field Settings</P>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                }}>
                <FormControl margin="none" size="small" sx={{ minWidth: "200px" }}>
                  <InputLabel variant="outlined" style={{ backgroundColor: "white" }}>
                    Review Config
                  </InputLabel>
                  <PSelect
                    onChange={(e) => {
                      setSelectedReviewConfigId(e.target.value as number);
                    }}
                    startAdornment={<SettingsIcon color="primary" />}
                    renderValue={(id) => allReviewConfigs.find((rc) => rc.id === id)?.name}
                    value={selectedReviewConfigId}>
                    {[
                      <MenuItem value={-1} key="none">
                        None
                      </MenuItem>,
                    ].concat(
                      allReviewConfigs
                        .slice(0)
                        .sort((a, b) => (a.name > b.name ? 1 : -1))
                        .map((rc) => (
                          <MenuItem value={rc.id} key={rc.id}>
                            {rc.name}
                          </MenuItem>
                        ))
                    )}
                  </PSelect>
                </FormControl>
                {sourceField ? null : (
                  <FormControl size="small" sx={{ minWidth: "200px" }}>
                    <InputLabel variant="outlined" style={{ backgroundColor: "white" }}>
                      Section
                    </InputLabel>
                    <PSelect
                      onChange={(e) => {
                        setSelectedSectionId(e.target.value as number);
                      }}
                      renderValue={(id) => sectionOptions.find((s) => s.id === id)?.name}
                      value={selectedSectionId}>
                      {sectionOptions
                        .slice(0)
                        .sort((a, b) => (a.name > b.name ? 1 : -1))
                        .map((s) => (
                          <MenuItem value={s.id} key={s.id}>
                            {s.name}
                          </MenuItem>
                        ))}
                    </PSelect>
                  </FormControl>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {isPending && (
          <Box
            sx={{
              py: 2,
            }}>
            <LinearProgress variant="determinate" value={(tasksCompleted / totalTasks) * 100} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          loading={isPending}
          variant="contained"
          onClick={handleImport}
          color={sourceField ? "error" : "primary"}
          disabled={error !== null || (!sourceField && selectedSectionId === undefined) || jsonField.current === null}>
          {sourceField ? "Merge" : "Import"}
        </LoadingButton>
      </DialogActions>
    </>
  );
};
